import './styles/Profile.scss';

import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Announcement, ExitToApp, NotificationsActive } from '@mui/icons-material';
import { Avatar, Badge, Grid, IconButton, Paper, Tooltip } from '@mui/material';
import * as Sentry from '@sentry/react';
import { isCompanyUser, isIOUser, isShibUser } from '@thro-helpers/users';
import { User } from '@thro-types/users';

import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { stringToColour } from 'utils';

import MARK_ALL_NOTIFICATIONS_AS_READ from 'gql/mutations/markAllNotificationsAsRead.gql';
import GET_SYSTEM_NOTIFICATIONS from 'gql/queries/MessagingService/getSystemNotifications.gql';
import GET_NOTIFICATIONS_FOR_USER_QUERY from 'gql/queries/getNotificationsForUser.gql';

import ContextMenu from 'app/components/ContextMenu';
import GQLGuard from 'app/components/GQLGuard';
import InfoBox from 'app/components/InfoBox';
import { LanguagePicker } from 'app/components/LanguagePicker';
import { getImageServerUrl } from 'app/components/Logo';
import Notification from 'app/containers/Messaging/Notifications';
import { SystemNotification } from 'app/containers/SystemNotifications/SystemNotification/NotificationTypes';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';

import { ShoppingCartButton } from './ShoppingCart';

interface Props {
  user: User;
}

export default function Profile({ user }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const client = useApolloClient();

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuSystemNotificiationsOpen, setMenuSystemNotificiationsOpen] = useState(false);
  const anchorRef = useRef(null);
  const anchorRefSystemNotifications = useRef(null);

  const variables =
    user && isCompanyUser(user)
      ? { userId: user.id, entitlements: [] }
      : !isIOUser(user) && { userId: user.uid, entitlements: user.entitlements };

  const aclHelper = new ACLHelper(user);
  const { data, error, loading } = useQuery<any, any>(GET_NOTIFICATIONS_FOR_USER_QUERY, {
    variables: variables,
    pollInterval: 60000,
    skip: !user || aclHelper.isInternationalOfficeUser()
  });
  const systemNotifications = useQuery<any, any>(GET_SYSTEM_NOTIFICATIONS);

  const [markAllNotificationsAsRead] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ);

  const handleLogout = async () => {
    const token = localStorage.getItem('jwt-token');
    localStorage.removeItem('jwt-token');
    localStorage.removeItem('refresh-token');
    await client.clearStore();
    client.cache.gc();
    Sentry.setUser(null);
    if (!token) {
      return;
    }
    if (isShibUser(user) && !aclHelper.isTestUser()) {
      window.location.href = `${import.meta.env.VITE_SHIB_LOGOUT_URL}?token=${token}`;
    } else {
      window.location.href = '/login';
    }
  };

  const getSubtitle = () => {
    if (aclHelper.isSuperUser()) return 'Dashboard Administrator';
    else if (isCompanyUser(user)) return user.company?.name;
    else if (isIOUser(user)) return t(translations.common.incomingsUser());
    else if (aclHelper.isJobmarketAdmin()) return t(translations.common.jobmarketAdministrator());
    else if (aclHelper.isIncomingsAdmin()) return t(translations.common.incomingsAdministrator());
    else if (aclHelper.isStudent())
      return `${user.role} ${
        user.communities.length > 0 ? '(' + user.communities.join(', ') + ')' : ''
      }`;
    return '---';
  };

  const [avatarBackground, color] = stringToColour(user.email);

  return (
    <Grid container className="user-profile" justifyContent="flex-end" flexWrap="nowrap">
      {location.pathname.startsWith('/leihbar') && <ShoppingCartButton />}
      {aclHelper.isInternationalOfficeUser() ? (
        <></>
      ) : loading || error ? (
        <Grid item xs="auto" className="user-profile__notifications" ref={anchorRef}>
          <NotificationsActive fontSize="large" color="disabled" />
        </Grid>
      ) : (
        <Grid item xs="auto" className="user-profile__notifications" ref={anchorRef}>
          <Badge
            badgeContent={data.getNotificationsForUser.filter(n => !n.viewedAt).length}
            color="primary"
            overlap="circular"
            onClick={() => {
              if (data.getNotificationsForUser.length > 0) {
                setMenuOpen(true);
              }
            }}
          >
            <NotificationsActive fontSize="large" />
          </Badge>
          <ContextMenu
            open={menuOpen}
            setOpen={setMenuOpen}
            anchorEl={anchorRef.current}
            placement="bottom"
            custom
          >
            <Grid
              container
              className="user-profile__notifications__list"
              component={Paper}
              elevation={10}
            >
              <Grid container className="user-profile__notifications__list__subheader">
                <Grid item xs>
                  {t(translations.pages.home.notifications.heading())}
                </Grid>
                <Grid
                  item
                  xs="auto"
                  className="user-profile__notifications__list__subheader__mark-all-as-read"
                  onClick={() => {
                    isShibUser(user) &&
                      markAllNotificationsAsRead({
                        variables: { userId: user.uid }
                      });
                  }}
                >
                  {t(translations.layout.profile.markAllAsRead())}
                </Grid>
              </Grid>
              <Grid container className="user-profile__notifications__list__content">
                {data.getNotificationsForUser.map((n, idx) => (
                  <Notification
                    notification={n}
                    isLast={idx + 1 === data.getNotificationsForUser.length}
                    key={n.id || idx}
                  />
                ))}
              </Grid>
            </Grid>
          </ContextMenu>
        </Grid>
      )}
      <Grid item xs="auto" className="user-profile__system-notifications">
        <GQLGuard
          query={systemNotifications}
          onError={() => {
            return <></>;
          }}
          hideLoader
        >
          {({
            systemNotifications: notifications
          }: {
            systemNotifications: SystemNotification[];
          }) => (
            <>
              {notifications.length > 0 && (
                <Grid
                  item
                  xs="auto"
                  className="user-profile__system-notifications"
                  ref={anchorRefSystemNotifications}
                >
                  <Badge
                    badgeContent={notifications.length}
                    color="primary"
                    overlap="circular"
                    onClick={() => {
                      if (notifications.length > 0) {
                        setMenuSystemNotificiationsOpen(true);
                      }
                    }}
                  >
                    <Announcement
                      fontSize="large"
                      className={clsx('user-profile__system-notifications__icon', {
                        'user-profile__system-notifications__icon--info': notifications.find(
                          notification => notification.type === 'info'
                        ),
                        'user-profile__system-notifications__icon--maintenance': notifications.find(
                          notification => notification.type === 'maintenance'
                        ),
                        'user-profile__system-notifications__icon--incident': notifications.find(
                          notification => notification.type === 'incident'
                        )
                      })}
                    />
                  </Badge>
                  <ContextMenu
                    open={menuSystemNotificiationsOpen}
                    setOpen={setMenuSystemNotificiationsOpen}
                    anchorEl={anchorRefSystemNotifications.current}
                    placement="bottom"
                    custom
                  >
                    <Grid
                      container
                      className="user-profile__system-notifications__list"
                      component={Paper}
                      elevation={10}
                    >
                      <Grid
                        container
                        className="user-profile__system-notifications__list__subheader"
                      >
                        <Grid item xs>
                          {t(translations.pages.systemNotificationsSettings.systemNotifications())}
                        </Grid>
                      </Grid>
                      <Grid container className="user-profile__system-notifications__list__content">
                        {notifications.map((notification, idx) => (
                          <InfoBox
                            type={notification.type}
                            title={notification.headline}
                            expandable
                            collapsed
                            key={idx}
                          >
                            {notification.content}
                          </InfoBox>
                        ))}
                      </Grid>
                    </Grid>
                  </ContextMenu>
                </Grid>
              )}
            </>
          )}
        </GQLGuard>
      </Grid>
      <Grid item xs="auto" className="user-profile__user-info">
        <Grid container className="user-profile__user-info__name">
          {user.firstName} {user.lastName}
        </Grid>
        <Grid container className="user-profile__user-info__company-name">
          {getSubtitle()}
        </Grid>
      </Grid>
      <Grid item xs="auto">
        {isCompanyUser(user) && user.company?.logo ? (
          <Avatar
            className="user-profile__avatar"
            src={getImageServerUrl(user.company.logo, 'jobmarket/logos')}
          />
        ) : (
          <Avatar
            className="user-profile__avatar"
            style={{
              backgroundColor: avatarBackground,
              color
            }}
          >
            {user.firstName.charAt(0).toUpperCase()}
            {user.lastName.charAt(0).toUpperCase()}
          </Avatar>
        )}
      </Grid>
      <Grid item xs="auto" className="user-profile__language">
        <LanguagePicker />
      </Grid>
      <Grid container item xs="auto" alignContent="center">
        <Tooltip
          title="Logout"
          placement="bottom"
          PopperProps={{
            modifiers: [{ name: 'flip', enabled: false }]
          }}
        >
          <IconButton color="primary" onClick={() => handleLogout()}>
            <ExitToApp fontSize="large" />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
}
